import { useMemo } from 'react';

export enum BookingStatus {
  Success = 'Success',
  Pending = 'Pending',
  Failed = 'Failed',
  Canceled = 'Canceled',
}
export type CancelBookingPayment = 'Free' | 'Payment';

export interface BookingDto {
  amount?: string;
  cancel_date?: string | null;
  canceled: boolean;
  day: string;
  end_time: string;
  id?: number;
  label_id?: number;
  payment_id?: number;
  place_id?: number;
  start_time: string;
  status: BookingStatus;
  table_id?: number;
  table_number: number;
  time_range_id: number;
  type_name?: string;
  user_id?: number;
}

/**
 * Возвращает объект, содержащий цвет статуса и текст статуса на основе предоставленного статуса бронирования/отмены бронирования.
 *
 * @param status - Статус бронирования/отмены бронирования
 * @return  Объект со следующими свойствами:
 *   - statusColor: Цвет, связанный со статусом
 *   - statusText: Текст, связанный со статусом
 */
export const useBookingStatus = (status: BookingStatus | CancelBookingPayment) => {
  return useMemo(
    () => ({
      statusColor: BOOKING_STATUS_MAP[status].color,
      statusText: getStatusText(status),
    }),
    [status],
  );
};

/**
 * Возвращает статус отмены бронирования в зависимости от переданного булевого значения.
 *
 * @param freeCancel - Показывает, является ли бронирование бесплатным или нет.
 * @return Статус отмены бронирования. Возможные значения: 'Free' или 'Payment'.
 */
export const getFreeCancelStatus = (freeCancel?: boolean): CancelBookingPayment => {
  if (freeCancel) {
    return 'Free';
  } else {
    return 'Payment';
  }
};

/**
 * Возвращает статус бронирования на основе предоставленной брони
 *
 * @param  booking - Объект бронирования
 * @return  Статус бронирования. Возможные значения: 'Success', 'Pending', 'Failed' или 'Canceled'
 */
export const getBookingStatus = (booking: BookingDto): BookingStatus => {
  if (booking.canceled) {
    return BookingStatus.Canceled;
  } else {
    return booking.status;
  }
};

const STATUS_COLORS = {
  green: '#2e7d32',
  blue: '#1976d2',
  red: '#d32f2f',
  gray: '#9e9e9e',
};

export const BOOKING_STATUS_MAP = {
  Success: { label: 'Оплачено', color: STATUS_COLORS.green, value: 'Success' },
  Pending: { label: 'В ожидании', color: STATUS_COLORS.blue, value: 'Pending' },
  Failed: { label: 'Ошибка оплаты', color: STATUS_COLORS.red, value: 'Failed' },
  Canceled: { label: 'Отменено', color: STATUS_COLORS.gray, value: 'Canceled' },
  Free: { label: 'Возврат средств', color: STATUS_COLORS.green, value: 'Free' },
  Payment: { label: 'Без возврата средств', color: STATUS_COLORS.red, value: 'Payment' },
};

/**
 * Возвращает соответствующий текст для заданного BookingStatus
 *
 * @param  status - Статус бронирования
 * @return Текст, соответствующий заданному статусу
 */
const getStatusText = (status: BookingStatus | CancelBookingPayment) => {
  switch (status) {
    case 'Success':
      return BOOKING_STATUS_MAP.Success.label;
    case 'Pending':
      return BOOKING_STATUS_MAP.Pending.label;
    case 'Failed':
      return BOOKING_STATUS_MAP.Failed.label;
    case 'Canceled':
      return BOOKING_STATUS_MAP.Canceled.label;
    case 'Free':
      return BOOKING_STATUS_MAP.Free.label;
    case 'Payment':
      return BOOKING_STATUS_MAP.Payment.label;
  }
};

export const getDayMonth = (day: string) => {
  const options: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  };

  const date = new Date(`${day}`);
  return date.toLocaleString('ru-RU', options);
};

export const getTime = (time: string) => {
  return time?.slice(0, 6)?.replace(/"/g, '');
};

export const getDate = (date: string) => {
  const day = new Date(date.replace(/"/g, ''));
  return day.toLocaleDateString('ru-RU');
};

// FIXME:не знаю как ты хочешь забирать это время, поэтому просто отдаю то, что приходит
export const getCanceledTime = (cancelDate: string) => {
  const timeRegex = /\d{2}:\d{2}:\d{2}/;

  const match = cancelDate.match(timeRegex);

  return match ? match[0] : null;
};

// FIXME:не знаю как ты хочешь забирать это время, поэтому просто отдаю то, что приходит
export const getRangeTime = (timeRange?: Date) => {
  const gmtString = timeRange?.toString();
  const timeRegex = /\d{2}:\d{2}/;

  const match = gmtString?.match(timeRegex);

  return match ? match[0] : null;
};

export const getWeekDay = (day: string) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
  };

  const date = new Date(`${day}`);
  return date.toLocaleString('ru-RU', options);
};

export const responseToMap = (response: { [key: number]: boolean }) => {
  const map: Map<number, boolean> = new Map();
  if (response) {
    Object.keys(response).forEach((t) => {
      map.set(Number(t), response[Number(t)]);
    });
  }
  return map;
};

export const bookingsFilterOptions: BookingOption[] = [
  {
    label: BOOKING_STATUS_MAP.Success.label,
    value: BOOKING_STATUS_MAP.Success.value,
    color: BOOKING_STATUS_MAP.Success.color,
  },
  {
    label: BOOKING_STATUS_MAP.Pending.label,
    value: BOOKING_STATUS_MAP.Pending.value,
    color: BOOKING_STATUS_MAP.Pending.color,
  },
  {
    label: BOOKING_STATUS_MAP.Failed.label,
    value: BOOKING_STATUS_MAP.Failed.value,
    color: BOOKING_STATUS_MAP.Failed.color,
  },
  {
    label: BOOKING_STATUS_MAP.Canceled.label,
    value: BOOKING_STATUS_MAP.Canceled.value,
    color: BOOKING_STATUS_MAP.Canceled.color,
  },
];

export const SUCCESS_OPTION = bookingsFilterOptions[0];
export const PENDING_OPTION = bookingsFilterOptions[1];
export const DEFAULT_OPTIONS = [SUCCESS_OPTION, PENDING_OPTION];

export interface BookingOption {
  label: string;
  value: string;
  color: string;
}

export interface GroupedBookings {
  start_time: string;
  end_time: string;
  table_number: number;
  booking_status: BookingStatus;
  bookings: BookingDto[];
  id: string;
}

interface GroupedData {
  [key: string]: BookingDto[][];
}

export const groupBookings = (bookings: BookingDto[]) => {
  const tableNumberAndBookingStatusGrouping = groupByTableNumberAndBookingStatus(bookings);
  const timerangeGrouping = groupByTimeRange(tableNumberAndBookingStatusGrouping);
  const resultGroupingWithInfo = addGroupInfo(timerangeGrouping);

  return resultGroupingWithInfo;
};

const groupByTableNumberAndBookingStatus = (bookings: BookingDto[]) => {
  return bookings.reduce(
    (acc, obj) => {
      // Создаем уникальный ключ на основе значений table_number и bookingStatus
      const bookingStatus = getBookingStatus(obj);
      const key = `${obj.table_number}-${bookingStatus}`;

      // Инициализируем массив, если его еще нет
      if (!acc[key]) {
        acc[key] = [];
      }

      // Добавляем объект в соответствующий массив
      acc[key].push(obj);

      return acc;
    },
    {} as { [key: string]: BookingDto[] },
  );
};

const groupByTimeRange = (groupedByStatusAndTableBookings: {
  [key: string]: BookingDto[];
}): GroupedData => {
  const result: GroupedData = {};

  Object.entries(groupedByStatusAndTableBookings).forEach(([key, items]) => {
    const groupedItems: BookingDto[][] = [];
    let currentGroup: BookingDto[] = [];

    items.forEach((item) => {
      if (
        currentGroup.length === 0 ||
        currentGroup[currentGroup.length - 1].time_range_id + 1 !== item.time_range_id
      ) {
        currentGroup = [item];
        groupedItems.push(currentGroup);
      } else {
        currentGroup.push(item);
      }
    });

    result[key] = groupedItems;
  });

  return result;
};

const addGroupInfo = (groupedData: GroupedData) => {
  const resultGrouping: GroupedBookings[] = [];
  for (const key in groupedData) {
    const group = groupedData[key];
    group.forEach((groupItem) => {
      const groupFirstBooking = groupItem[0];
      const groupLastBooking = groupItem[groupItem.length - 1];
      const bookingStatus = getBookingStatus(groupFirstBooking);
      resultGrouping.push({
        start_time: groupFirstBooking.start_time,
        end_time: groupLastBooking.end_time,
        table_number: groupFirstBooking.table_number,
        booking_status: bookingStatus,
        bookings: groupItem,
        id: `${groupFirstBooking.start_time}-${groupLastBooking.end_time}-${groupFirstBooking.table_number}-${bookingStatus}`,
      });
    });
  }

  return resultGrouping;
};
